import type { AnyValue } from "@/lib/rowTypes";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useImageOverlay, type ImageBoxPosition } from "@/hooks/useImageOverlay";
import { useIsMobile } from "@/hooks/use-mobile";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useWallet, notifyWalletUpdated } from "@/hooks/useWallet";
import { useEventEngine } from "@/hooks/useEventEngine";
import { useLifeEvents } from "@/hooks/useLifeEvents";
import LifeEventCard from "@/components/events/LifeEventCard";
import { usePlayerProgression } from "@/hooks/usePlayerProgression";
import LevelUpModal from "@/components/LevelUpModal";
import { useStreaks } from "@/hooks/useStreaks";
import { useMissions } from "@/hooks/useMissions";
import { useLessonProgress } from "@/hooks/useLessonProgress";
import WalletDisplay from "@/components/WalletDisplay";
import VideoStage from "@/components/lesson/VideoStage";
import InfographicStage from "@/components/lesson/InfographicStage";
import QuestionCard from "@/components/lesson/QuestionCard";
import AdaptiveAssessmentStage from "@/components/lesson/adaptive/AdaptiveAssessmentStage";
import { useV3AssessmentEnabled } from "@/hooks/useV3Assessment";
import CaseStudyReadingStage from "@/components/lesson/CaseStudyReadingStage";
import LessonStageNav, { type LessonStageType } from "@/components/lesson/LessonStageNav";
import MasteryLessonContent from "@/components/lesson/MasteryLessonContent";
import DecisionCheckStage, { shuffleDecideCheck } from "@/components/lesson/DecisionCheckStage";
import { getCuratedDecideCheck } from "@/data/launchDecideQuestions";
import CompletionTakeaway from "@/components/lesson/CompletionTakeaway";
import ExploreModal from "@/components/lesson/ExploreModal";
import { lumiPlayback } from "@/lib/audio/lumiPlaybackController";
import { useMasteryContent } from "@/hooks/useMasteryContent";
import { recordLearningEvent } from "@/lib/learning-engine/eventLogger";
import { useQueryClient } from "@tanstack/react-query";
import { getCaseStudyReading } from "@/data/caseStudyReadings";
import {
  buildAdaptiveSequence,
  buildAdaptivePlan,
  pickRescueReplacement,
  shiftTier,
  tierOfQuestion,
  isBetaLesson,
  resolveStartingTier,
  type AdaptiveTier,
  type AdaptiveQuestion,
} from "@/lib/learning/adaptiveQuestionSelector";
import { trackQuiz } from "@/lib/learning/telemetry";
import { trackLearningEventOnce } from "@/lib/analytics/learning";
import { useLessonVoiceMomentLifecycle } from "@/hooks/useLessonVoiceMoment";
import ExplanationVideo from "@/components/lesson/ExplanationVideo";
import EventCard from "@/components/events/EventCard";
import AIRemediationCard from "@/components/ai/AIRemediationCard";
import AIQuizExplanationPanel from "@/components/ai/AIQuizExplanationPanel";
import AITutorDrawer from "@/components/ai/AITutorDrawer";
import QuickHintButton from "@/components/ai/QuickHintButton";
import { ArrowLeft, ArrowRight, Trophy, Save, Sparkles, Globe2, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import classroomBg from "@/assets/classroom-bg.png";
import { useSfx } from "@/hooks/useSfx";
import { useSceneMusic } from "@/hooks/useSceneMusic";
import type { MusicKey } from "@/lib/audio/musicMap";
import { useAccessTier } from "@/hooks/useAccessTier";
import { useLessonReleaseGate } from "@/hooks/useLessonReleaseGate";
import { useLessonStartGate } from "@/hooks/useLessonStartGate";
import { DailyLessonLockScreen } from "@/components/lesson/DailyLessonLockScreen";
import { isLaunchLessonId } from "@/lib/launch/launchExperienceRoster";
import {
  didPassRequiredQuiz,
  getDeferredBetaQuizReward,
  getNextBetaPathItemAfterLesson,
  getNextLaunchRouteAfterLesson,
  shouldBlockRequiredBetaQuiz,
  shouldDeferPerQuestionQuizReward,
} from "@/lib/launch/launchProgression";
import { useLaunchPath } from "@/hooks/useLaunchPath";
import { resolveNextLessonRoute } from "@/lib/progression/getNextLessonRoute";
import LaunchLifeEventOverlay from "@/components/launch/LaunchLifeEventOverlay";
import { getLifeEventForLesson, hasSeenLifeEvent } from "@/data/launchLifeEvents";
import { LumiCoachingCard } from "@/components/lumi/LumiCoachingCard";
import {
  getLumiLessonIntro,
  getLumiLessonOutcome,
} from "@/data/lumiLaunchCoaching";
import { getLumiLessonAudioTexts } from "@/data/lumiLessonAudioManifest";
import { preloadLumiTexts } from "@/lib/audio/lumiAudioCache";
import LumiHolocard from "@/components/lessons/LumiHolocard";
import { getLumiHolocardMomentsFor } from "@/lib/audio/lumiHolocardVoiceMap";
import RetakeLessonDialog from "@/components/lesson/RetakeLessonDialog";
import { useLessonRetake } from "@/hooks/useLessonRetake";
import { assertFreshSession, runWithSessionRetry } from "@/lib/auth/sessionGuard";
import { getErrorMessage } from "@/lib/errors";
import { resolveMediaUrl } from "@/lib/media/signedMediaUrl";
import { resolveInitialLessonStage } from "@/lib/lessons/resolveInitialLessonStage";

interface Question {
  question_id: string;
  question: string;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string;
  explanation: string | null;
  explanation_video_url: string | null;
}

/* ─── Shuffle helper: randomize answer positions per question.
 * The correct answer is NOT known to the client — server returns it on grade.
 * We track each display option's original letter so we can submit it.
 */
function shuffleOptions(q: Question): { options: { key: string; text: string; originalKey: string }[] } {
  const entries = [
    { originalKey: "A", text: q.option_a },
    { originalKey: "B", text: q.option_b },
    { originalKey: "C", text: q.option_c },
    { originalKey: "D", text: q.option_d },
  ];
  for (let i = entries.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [entries[i], entries[j]] = [entries[j], entries[i]];
  }
  const labels = ["A", "B", "C", "D"];
  const options = entries.map((e, i) => ({ key: labels[i], text: e.text, originalKey: e.originalKey }));
  return { options };
}

interface ShuffledQuestion {
  original: Question;
  options: { key: string; text: string; originalKey: string }[];
  correctKey: string; // display key; "" until server grades the submitted answer
}

type LessonStage = "video" | "infographic" | "reading" | "mastery" | "decision" | "questions";

const LessonScreen = () => {
  const { lessonId } = useParams<{ lessonId: string }>();
  // Beta lesson voice-moment lifecycle: preload signed URLs for the
  // active lesson's mentor moments and reset fire-once state on change.
  useLessonVoiceMomentLifecycle(lessonId);
  // Preload all approved Lumi voice lines this lesson may surface so the
  // speaker button plays instantly. No-op (and silent on failure) for
  // lessons with no manifest entries.
  useEffect(() => {
    if (!lessonId) return;
    const texts = getLumiLessonAudioTexts(lessonId);
    if (texts.length === 0) return;
    void preloadLumiTexts(texts, lessonId);
  }, [lessonId]);
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { balance, addFunds, refetch } = useWallet();
  const { activeEvent, multiplier, checkAndTriggerEvent, resolveEvent, dismissEvent } = useEventEngine();
  const lifeEvents = useLifeEvents();
  const { playerLevel, awardXP, lastAward, clearLastAward } = usePlayerProgression();
  const { recordLearning } = useStreaks();
  const { incrementProgress } = useMissions();
  const { savedProgress, loaded: progressLoaded, loadProgress, saveProgress, clearProgress } = useLessonProgress(lessonId);
  const masteryContent = useMasteryContent(lessonId);
  const navigate = useNavigate();
  const { v3Enabled, v3FlagLoading } = useV3AssessmentEnabled();
  const location = useLocation();
  const { playSfx } = useSfx();
  const { tier: accessTier } = useAccessTier();
  // Production-experience release gate: a paying learner may only open a
  // lesson that passes the canonical lesson-experience contract. Admins keep
  // full catalog access for content review. The database enforces the same
  // rule on progress writes.
  const lessonRelease = useLessonReleaseGate(lessonId);
  // One canonical lesson per learner-local day, checked BEFORE any lesson
  // work begins. The server also enforces it on start and completion writes.
  const lessonStartGate = useLessonStartGate(lessonId);
  // Dashboard is the source of truth for beta path ordering. Reuse
  // useLaunchPath so the "Continue to Next Lesson" button navigates to the
  // same next item the dashboard's Launch Path card recommends (which
  // correctly interleaves the three beta modules — Budget Builder, Credit
  // Management, Scam Defense — between lessons).
  const { items: launchPathItems, loading: launchPathLoading } = useLaunchPath();
  const nextLaunchItem = useMemo(
    () => getNextBetaPathItemAfterLesson(launchPathItems, lessonId),
    [launchPathItems, lessonId],
  );
  const nextBetaRoute = useMemo(
    () => getNextLaunchRouteAfterLesson(launchPathItems, lessonId),
    [launchPathItems, lessonId],
  );
  const hasNextBeta = !!nextBetaRoute;
  const nextBetaRouteUnavailable = !!nextLaunchItem && !nextBetaRoute;
  const nextBetaIsModule = nextLaunchItem?.itemType === "simulator";
  const continueButtonLabel = nextBetaIsModule
    ? "Continue to Next Module"
    : "Continue to Next Lesson";
  const navigateToNextBetaRoute = useCallback(() => {
    if (!nextBetaRoute) {
      toast.error("Next step unavailable", {
        description: nextBetaRouteUnavailable
          ? "The next launch path item is misconfigured. Please return to the dashboard."
          : launchPathLoading
            ? "The next launch path item is still loading. Please try again."
            : "There is no next launch path item.",
      });
      return false;
    }
    navigate(nextBetaRoute);
    return true;
  }, [launchPathLoading, navigate, nextBetaRoute, nextBetaRouteUnavailable]);
  const [lessonsCompleted, setLessonsCompleted] = useState(0);
  const isKnowledgeCheckContext = useMemo(() => {
    const search = new URLSearchParams(location.search);
    const mode = search.get("mode") || search.get("stage");
    return location.pathname.includes("/knowledge-check") || mode === "knowledge-check";
  }, [location.pathname, location.search]);

  // The curated 13-node launch experience (Lumi coaching, adaptive rescue,
  // launch-path routing) applies to every learner. Admins get the full
  // legacy catalog behaviour for content review.
  const isLaunchExperience = accessTier !== "admin";
  const [showLevelUp, setShowLevelUp] = useState(false);
  const [pendingLifeEvent, setPendingLifeEvent] = useState<ReturnType<typeof getLifeEventForLesson>>(null);
  // Dedupe guard: ensure the level-up modal can fire AT MOST ONCE per
  // (learner × lesson × attempt). Without this, both the auto-advance path
  // and downstream effects could surface the same level-up twice.
  const levelUpShownRef = useRef<Set<string>>(new Set());
  // Dedupe guard for the lesson-complete reward flow itself. Prevents
  // duplicate XP awards / reward toasts when handleNext is reached more
  // than once for the same final answer (e.g. clicking + effect retry).
  const lessonRewardFiredRef = useRef<Set<string>>(new Set());

  const [stage, setStage] = useState<LessonStage>("video");
  const lessonSceneKey: MusicKey =
    stage === "video" ? "lessonBed"
    : stage === "infographic" || stage === "reading" ? "infographicFocus"
    : stage === "decision" ? "decisionPoint"
    : stage === "mastery" ? "quizFocus"
    : "quizFocus";
  useSceneMusic(lessonSceneKey);

  // Prefetch Applied Decision Check questions as soon as the lesson loads so
  // the Decide tab opens instantly regardless of how fast the learner moves.
  useEffect(() => {
    if (!lessonId) return;
    // Beta lessons use curated, deterministic Decide questions — no AI prefetch needed.
    if (getCuratedDecideCheck(lessonId)) return;
    const cacheKey = `decide-check:${lessonId}`;
    if (sessionStorage.getItem(cacheKey)) return;
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke(
          "generate-decide-questions",
          { body: { lessonId } },
        );
        if (cancelled || error) return;
        if (data?.questions?.length === 3) {
          const shuffled = shuffleDecideCheck(data);
          sessionStorage.setItem(cacheKey, JSON.stringify(shuffled));
        }
      } catch {
        /* prefetch is best-effort */
      }
    })();
    return () => { cancelled = true; };
  }, [lessonId]);

  const [lessonName, setLessonName] = useState("");
  const [courseName, setCourseName] = useState("");
  const [lessonVideoUrl, setLessonVideoUrl] = useState<string | null>(null);
  const [lessonInfographicUrl, setLessonInfographicUrl] = useState<string | null>(null);
  const [rewardPerCorrect, setRewardPerCorrect] = useState(5);
  const [completionBonus, setCompletionBonus] = useState(25);
  const [perfectScoreBonus, setPerfectScoreBonus] = useState(15);
  /** Actual server-awarded persistent payout for this lesson (max $20). */
  const [lessonPayout, setLessonPayout] = useState(0);
  const [lessonOrder, setLessonOrder] = useState<number | null>(null);
  const [betaStepOrder, setBetaStepOrder] = useState<number | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [shuffledQuestions, setShuffledQuestions] = useState<ShuffledQuestion[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [showAICoaching, setShowAICoaching] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [isFailed, setIsFailed] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [showExplanationVideo, setShowExplanationVideo] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  // Entry-stage race fix: initial stage waits until media availability is known (bounded).
  const [mediaSettled, setMediaSettled] = useState(false);
  const mediaLessonRef = useRef(lessonId);
  useEffect(() => {
    setMediaSettled(false);
    mediaLessonRef.current = lessonId;
  }, [lessonId]);
  const [quizLoadError, setQuizLoadError] = useState<string | null>(null);
  const [autoCompleteTriggered, setAutoCompleteTriggered] = useState(false);
  const [exploreOpen, setExploreOpen] = useState(false);
  const [answeredIds, setAnsweredIds] = useState<Set<string>>(new Set());
  const [completedStages, setCompletedStages] = useState<Set<LessonStageType>>(new Set());
  const [resumeApplied, setResumeApplied] = useState(false);
  const [showTutor, setShowTutor] = useState(false);
  const [wrongAnswers, setWrongAnswers] = useState<{ question: string; selectedAnswer: string; correctAnswer: string }[]>([]);
  const [wrongQuestionIds, setWrongQuestionIds] = useState<Set<string>>(new Set());
  const [hintUsageCount, setHintUsageCount] = useState(0);
  const [retakeDialogOpen, setRetakeDialogOpen] = useState(false);
  const { startRetake, finalizeRetake, pending: retakePending } = useLessonRetake();

  const handleConfirmLessonRetake = useCallback(async () => {
    if (!lessonId) return;
    try {
      await startRetake(lessonId);
      setRetakeDialogOpen(false);
      // Reload the page so all lesson state re-initialises from the freshly
      // reset lesson_progress row. Preserves URL. No wallet/XP/badge changes.
      window.location.reload();
    } catch {
      /* toast already surfaced */
    }
  }, [lessonId, startRetake]);

  const recordQuizAttempt = useCallback(async (finalCorrectCount: number, passed: boolean) => {
    if (!user || !lessonId) return null;
    const incorrectQuestionIds = Array.from(wrongQuestionIds);
    const { data, error } = await supabase.rpc("record_lesson_quiz_attempt", {
      p_lesson_id: lessonId,
      p_course_id: courseName || "unknown",
      p_score_raw: finalCorrectCount,
      p_score_total: questions.length,
      p_incorrect_question_ids: incorrectQuestionIds,
      p_incorrect_concepts: [],
      p_wrong_answers: wrongAnswers,
      // Per-competency evidence: the server maps each answered item to its
      // canonical competency and derives correctness from the stored attempt.
      p_answered_question_ids: questions.map((q) => q.question_id),
    });
    if (error) {
      // Server-enforced one-lesson-per-day rule (learner's local day).
      if (`${getErrorMessage(error) ?? ""}`.includes("daily_lesson_limit_reached")) {
        toast.info("That's today's lesson", {
          description: "You've already finished a lesson today. Your next one unlocks tomorrow morning.",
        });
      }
      throw error;
    }
    await finalizeRetake(lessonId, finalCorrectCount, passed);
    // Adaptive Learning Engine mastery signal: record a real quiz-based
    // mastery + confidence update on the learner's lesson concept.
    // Server-side function performs an EWMA blend; never fakes signals,
    // never grants XP, coins, or unlocks.
    try {
      await (supabase).rpc("update_lesson_mastery_from_quiz", {
        p_lesson_id: lessonId,
        p_course_id: courseName || "unknown",
        p_score_raw: finalCorrectCount,
        p_score_total: questions.length,
        p_passed: passed,
      });
    } catch (masteryErr) {
      console.warn("[learning-engine] update_lesson_mastery_from_quiz failed", masteryErr);
    }
    // Measure Effectiveness: compare this independent attempt against the
    // evidence recorded before any intervention delivered for this lesson.
    try {
      await (supabase as never as { rpc: (n: string, a: AnyValue) => Promise<AnyValue> })
        .rpc("evaluate_intervention_effectiveness", { p_lesson_id: lessonId });
    } catch (effErr) {
      console.warn("[learning-engine] evaluate_intervention_effectiveness failed", effErr);
    }

    // Adaptive Learning Engine: log the quiz outcome and invalidate the
    // next-action query so the Dashboard re-resolves after the learner
    // finishes the quiz. Never blocks completion on logging failure.
    void recordLearningEvent({
      eventType: passed ? "quiz_passed" : "quiz_failed",
      lessonId,
      metadata: {
        score_raw: finalCorrectCount,
        score_total: questions.length,
        course_id: courseName || "unknown",
      },
    }).finally(() => {
      queryClient.invalidateQueries({ queryKey: ["heirwise-adaptive-learning-engine"] });
    });
    return data as { attempt_number: number; score_percent: number; passed: boolean };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: effect is keyed to identity/mount only; the omitted values are render-unstable inputs and including them would re-run the fetch/side effect on every render
  }, [courseName, finalizeRetake, lessonId, questions.length, user, wrongAnswers, wrongQuestionIds, queryClient]);
  const [questionStartedAt, setQuestionStartedAt] = useState<number>(() => Date.now());
  // Adaptive rescue state — beta lessons only.
  const [adaptivePlannedTiers, setAdaptivePlannedTiers] = useState<AdaptiveTier[]>([]);
  const [adaptiveServedTiers, setAdaptiveServedTiers] = useState<AdaptiveTier[]>([]);
  const [adaptiveRemaining, setAdaptiveRemaining] = useState<Record<AdaptiveTier, AdaptiveQuestion[]> | null>(null);
  const [adaptiveStreak, setAdaptiveStreak] = useState<{ correct: number; miss: number }>({ correct: 0, miss: 0 });

  const { getBoxPixels, ready: overlayReady } = useImageOverlay(classroomBg);

  // Load saved progress
  useEffect(() => {
    void (async () => {
      // Refresh JWT before any lesson writes to prevent silent RLS rejections
      // when the token has expired while the tab was backgrounded.
      await assertFreshSession();
      await loadProgress();
    })();
  }, [loadProgress]);

  useEffect(() => {
    if (accessTier === "admin") return;
    if (lessonRelease.loading) return;
    if (lessonRelease.ready) return;
    toast.info("This lesson is still in development.", {
      description: "It is not part of your released curriculum yet.",
    });
    navigate("/dashboard", { replace: true });
  }, [accessTier, lessonRelease.loading, lessonRelease.ready, navigate]);

  useEffect(() => {
    const load = async () => {
      if (!lessonId) return;
      setQuizLoadError(null);
      const { data: lesson } = await supabase
        .from("lessons")
        .select("lesson_name, course_name, lesson_video_url, lesson_infographic_url, reward_per_correct, completion_bonus, perfect_score_bonus, lesson_order, beta_order")
        .eq("lesson_id", lessonId)
        .single();

      setLessonName(lesson?.lesson_name || "");
      setCourseName(lesson?.course_name || "");
      // Gated curriculum media is served through short-lived signed URLs.
      const MEDIA_SETTLE_TIMEOUT_MS = 6000;
      const videoP = resolveMediaUrl(lesson?.lesson_video_url).then(setLessonVideoUrl).catch(() => undefined);
      const visualP = resolveMediaUrl(lesson?.lesson_infographic_url).then(setLessonInfographicUrl).catch(() => undefined);
      void Promise.race([
        Promise.allSettled([videoP, visualP]),
        new Promise((r) => setTimeout(r, MEDIA_SETTLE_TIMEOUT_MS)),
      ]).then(() => {
        // Ignore a late settle from a previously viewed lesson.
        if (mediaLessonRef.current === lessonId) setMediaSettled(true);
      });

      setRewardPerCorrect((lesson)?.reward_per_correct ?? 5);
      setCompletionBonus((lesson)?.completion_bonus ?? 25);
      setPerfectScoreBonus((lesson)?.perfect_score_bonus ?? 15);
      setLessonOrder((lesson)?.lesson_order ?? null);
      setBetaStepOrder((lesson)?.beta_order ?? null);

      const hasVideo = !!lesson?.lesson_video_url;
      const hasInfographic = !!lesson?.lesson_infographic_url;
      const isCaseStudy = lesson?.course_name === "Company Case Studies";


      const { data: questionsData, error: questionsError } = await supabase
        .from("questions")
        .select("*")
        .eq("lesson_id", lessonId);
      if (questionsError) {
        setQuizLoadError(questionsError.message || "Question lookup failed.");
        console.error("[lesson.quiz.load] Failed to load quiz questions", {
          lessonId,
          lessonName: lesson?.lesson_name,
          error: questionsError,
        });
      }
      const rawQuestions = (((questionsData as (Question & { active?: boolean | null })[]) || [])
        .filter((q) => q.active !== false)) as Question[];

      const uniqueQuestions = rawQuestions.filter((q, i, arr) =>
        arr.findIndex((x) => x.question_id === q.question_id) === i
      );

      // Beta lessons use the new 4-tier adaptive question bank
      // (easy / medium / hard / expert) driven by learner history.
      // Non-beta lessons keep the legacy foundation/guided/advanced ordering.
      let sortedQuestions: Question[];
      if (isBetaLesson(lessonId)) {
        const startTier: AdaptiveTier = await resolveStartingTier(user?.id, lessonId);
        const plan = buildAdaptivePlan(uniqueQuestions as AnyValue, startTier);
        sortedQuestions = (plan.sequence as AnyValue as Question[]).length > 0
          ? (plan.sequence as AnyValue as Question[])
          : uniqueQuestions;
        setAdaptivePlannedTiers(plan.plannedTiers);
        setAdaptiveServedTiers([...plan.plannedTiers]);
        setAdaptiveRemaining(plan.remainingByTier);
        setAdaptiveStreak({ correct: 0, miss: 0 });
      } else {
        const diffOrder: Record<string, number> = { foundation: 0, guided: 1, advanced: 2 };
        // Non-beta lessons: sort by the question's own difficulty tag
        // (foundation → guided → advanced). The legacy learner-tier filter
        // was removed with the mastery-tier system (Phase 2C).
        sortedQuestions = [...uniqueQuestions].sort((a, b) => {
          const da = diffOrder[(a as AnyValue).difficulty || "guided"] ?? 1;
          const db = diffOrder[(b as AnyValue).difficulty || "guided"] ?? 1;
          return da - db;
        });
      }

      setQuestions(sortedQuestions);
      setShuffledQuestions(sortedQuestions.map((q) => {
        const { options } = shuffleOptions(q);
        return { original: q, options, correctKey: "" };
      }));

      // --- Beta path quiz coverage audit (dev/QA aid) ---
      // Logs a structured coverage report whenever a beta lesson loads so we
      // never silently complete a beta lesson with zero knowledge-check
      // questions again (see "Opportunity Cost 0/0" regression).
      if (isLaunchLessonId(lessonId)) {
        const report = {
          betaPathItem: lesson?.lesson_name,
          lessonId,
          hasVideo,
          hasVisual: hasInfographic,
          rawQuestionCount: rawQuestions.length,
          activeQuestionCount: uniqueQuestions.length,
          adaptiveSequenceLength: sortedQuestions.length,
          completionAllowed: sortedQuestions.length >= 1,
          quizLoadError: questionsError?.message ?? null,
          status: questionsError || sortedQuestions.length === 0 ? "BLOCKED_MISSING_QUIZ" : "OK",
        };
        if (questionsError || sortedQuestions.length === 0) {
          console.error("[Beta Coverage Audit] Missing quiz questions for lesson:", lesson?.lesson_name, report);
        } else {
          console.info("[Beta Coverage Audit]", report);
        }
      }

      if (user) {
        const { count } = await supabase
          .from("user_progress")
          .select("*", { count: "exact", head: true })
          .eq("user_id", user.id)
          .eq("completed", true);
        setLessonsCompleted(count || 0);
      }
      setDataLoaded(true);
    };
    load();
  }, [lessonId, user]);


  const caseStudyReading = useMemo(
    () => courseName === "Company Case Studies" ? getCaseStudyReading(lessonName) : null,
    [courseName, lessonName]
  );

  // Available stages for nav
  const isCaseStudy = courseName === "Company Case Studies";

  const hasMasteryContent = !masteryContent.loading && (masteryContent.vocabulary.length > 0 || masteryContent.bookInsights.length > 0 || masteryContent.companyCases.length > 0);
  const hasDecisionPrompt = !masteryContent.loading && !!masteryContent.decisionPrompt;

  // P1-4 dev-only warning: surface beta lessons that resolve without any
  // mastery content so admins catch regressions early. No user-facing
  // change; never runs in production builds.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (masteryContent.loading) return;
    if (!isLaunchExperience) return;
    if (!lessonId || !isLaunchLessonId(lessonId)) return;
    if (hasMasteryContent) return;
    console.warn(
      "[beta.mastery.missing] Beta lesson has no vocabulary / book insight / company case",
      { lessonId, lessonName },
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: effect is keyed to identity/mount only; the omitted values are render-unstable inputs and including them would re-run the fetch/side effect on every render
  }, [accessTier, hasMasteryContent, lessonId, lessonName, masteryContent.loading]);

  const availableStages = useMemo(() => {
    const stages: LessonStageType[] = [];
    if (isCaseStudy) {
      if (caseStudyReading) stages.push("reading");
      // Media is optional: only offer the visual stage when an asset exists.
      if (lessonInfographicUrl) stages.push("infographic");
      if (hasMasteryContent) stages.push("mastery");
      if (hasDecisionPrompt) stages.push("decision");
      stages.push("questions");
    } else {
      if (lessonVideoUrl) stages.push("video");
      if (lessonInfographicUrl) stages.push("infographic");
      if (hasMasteryContent) stages.push("mastery");
      if (hasDecisionPrompt) stages.push("decision");
      stages.push("questions");
    }
    return stages;
  }, [lessonVideoUrl, lessonInfographicUrl, caseStudyReading, isCaseStudy, hasMasteryContent, hasDecisionPrompt]);

  // Apply saved progress or start each lesson from its first available stage
  useEffect(() => {
    if (!dataLoaded || !progressLoaded || !mediaSettled || resumeApplied || masteryContent.loading || availableStages.length === 0) return;

    setResumeApplied(true);

    if (savedProgress) {
      const savedStage = savedProgress.current_stage as LessonStageType;
      const savedStageIndex = availableStages.indexOf(savedStage);

      if (savedStageIndex >= 0) {
        setStage(savedStage as LessonStage);
        setCurrentIndex(savedProgress.current_question_index);
        setCorrectCount(savedProgress.correct_count);
        setAnsweredIds(new Set(savedProgress.answered_question_ids));
        setCompletedStages(new Set(availableStages.slice(0, savedStageIndex)));
        toast.info("Resumed from where you left off");
        return;
      }
    }

    const engineModality = (location.state as { engineModality?: string } | null)?.engineModality ?? null;
    setStage(resolveInitialLessonStage({ availableStages, engineModality, isCaseStudy }) as LessonStage);
    setCompletedStages(new Set());
  // eslint-disable-next-line react-hooks/exhaustive-deps -- location.state/isCaseStudy are read once at entry
  }, [dataLoaded, progressLoaded, mediaSettled, resumeApplied, masteryContent.loading, availableStages, savedProgress]);

  /* ---------------- PostHog learning analytics (canonical events) ----------------
   * Fired from authoritative state transitions only, and deduped per
   * (learner × lesson) progression sequence so rerenders / remounts /
   * StrictMode / returning to a completed stage cannot double-count.
   */
  const learningCtx = useMemo(
    () => ({
      lessonId: lessonId ?? null,
      lessonTitle: lessonName || null,
      lessonOrder,
      courseTitle: courseName || null,
      isBetaPath: lessonId ? isLaunchLessonId(lessonId) : null,
      betaStepOrder,
    }),
    [lessonId, lessonName, lessonOrder, courseName, betaStepOrder],
  );
  // Per-attempt analytics scope. A fresh mount (entering the lesson again)
  // and each quiz retry both count as new attempts, so the dedupe registry
  // in `learning.ts` suppresses only true duplicates (rerenders, remounts
  // within the same attempt) and never swallows a genuine repeat attempt —
  // mirrors the per-attempt `attemptId` scoping used for module events.
  const mountAttemptIdRef = useRef<string>(crypto.randomUUID());
  const [attemptSeq, setAttemptSeq] = useState(0);
  const learningScope = `${user?.id ?? "anon"}:${lessonId ?? "no-lesson"}:${mountAttemptIdRef.current}:${attemptSeq}`;

  // lesson_started — once the lesson experience has genuinely resolved
  // (content loaded and the learner's opening stage applied).
  useEffect(() => {
    if (!lessonId || !resumeApplied || !dataLoaded || !lessonName) return;
    trackLearningEventOnce("lesson_started", learningScope, { ...learningCtx, stage });
  }, [lessonId, resumeApplied, dataLoaded, lessonName, learningScope, learningCtx, stage]);

  // Stage-reached events — driven by the authoritative `stage` state.
  useEffect(() => {
    if (!lessonId || !resumeApplied || !dataLoaded || !lessonName) return;
    const event =
      stage === "video" ? "video_viewed"
      : stage === "infographic" ? "infographic_viewed"
      : stage === "decision" ? "decide_started"
      : stage === "questions" ? "quiz_started"
      : null;
    if (!event) return;
    trackLearningEventOnce(event, learningScope, { ...learningCtx, stage });
  }, [stage, lessonId, resumeApplied, dataLoaded, lessonName, learningScope, learningCtx]);


  // Save & Exit handler — canonical rule: ALWAYS save the learner's current
  // progress and return them to the main dashboard. Never use history.back,
  // never route to the course/lesson list, never route to an intermediate
  // page. Applies from every lesson sub-screen (video/visual/insight/decide/
  // completion) and while the Explore modal is open.
  const handleSaveAndExit = useCallback(async () => {
    try {
      await saveProgress(stage, currentIndex, correctCount, Array.from(answeredIds));
    } catch (err) {
      console.warn("[save-and-exit] progress save failed", err);
    }
    // Stop any active Lumi/audio playback so it never leaks past navigation.
    try { lumiPlayback.stop(); } catch { /* noop */ }
    toast.success("Progress saved!");
    void recordLearningEvent({
      eventType: "dashboard_returned",
      lessonId: lessonId ?? null,
      metadata: { source: "save_and_exit", stage },
    });
    navigate("/dashboard", { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: effect is keyed to identity/mount only; the omitted values are render-unstable inputs and including them would re-run the fetch/side effect on every render
  }, [stage, currentIndex, correctCount, answeredIds, saveProgress, navigate]);

  // Auto-save on stage change
  useEffect(() => {
    if (!dataLoaded || !resumeApplied || isComplete) return;
    saveProgress(stage, currentIndex, correctCount, Array.from(answeredIds));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: effect is keyed to identity/mount only; the omitted values are render-unstable inputs and including them would re-run the fetch/side effect on every render
  }, [stage, currentIndex]);

  const handleStageChange = useCallback((newStage: LessonStageType) => {
    // Enforce sequential gating: only allow if all prior stages are completed
    const targetIdx = availableStages.indexOf(newStage);
    const allPriorCompleted = availableStages.slice(0, targetIdx).every(s => completedStages.has(s));
    if (!allPriorCompleted && newStage !== stage) {
      toast.info("Complete the current stage first before moving ahead.");
      return;
    }
    setCompletedStages(prev => new Set([...prev, stage as LessonStageType]));
    setStage(newStage as LessonStage);
  }, [stage, availableStages, completedStages]);

  // Helper: advance to next stage after current
  const advanceFromStage = useCallback((currentStageName: LessonStageType) => {
    playSfx("soft_swoosh");
    setCompletedStages(prev => new Set([...prev, currentStageName]));
    const idx = availableStages.indexOf(currentStageName);
    const next = availableStages[idx + 1];
    if (next) {
      setStage(next as LessonStage);
    } else {
      setStage("questions");
    }
  }, [availableStages, playSfx]);

  const handleVideoComplete = () => advanceFromStage("video");
  const handleInfographicComplete = () => advanceFromStage("infographic");
  const handleReadingComplete = () => advanceFromStage("reading");
  const handleMasteryComplete = () => advanceFromStage("mastery");
  // decide_completed: only when the learner actually finishes the Decide stage.
  const handleDecisionComplete = () => {
    trackLearningEventOnce("decide_completed", learningScope, { ...learningCtx, stage: "decision" });
    advanceFromStage("decision");
  };

  const currentQuestion = questions[currentIndex];
  const currentShuffled = shuffledQuestions[currentIndex];
  const options = currentShuffled?.options || [];
  const correctKey = currentShuffled?.correctKey || "";
  const responseTimeMs = showExplanation ? Math.max(0, Date.now() - questionStartedAt) : null;

  // On the final quiz question, skip the intermediate "Complete Lesson"
  // affordance and the AI explanation panel — auto-advance to the official
  // Lesson Complete screen after a brief feedback moment.
  const isLastQuestion = questions.length > 0 && currentIndex === questions.length - 1;

  const handleAnswer = useCallback(
    async (key: string) => {
      if (selectedAnswer || !currentQuestion || !user || !lessonId) return;
      setSelectedAnswer(key);
      setShowExplanation(true);
      setAnsweredIds(prev => new Set([...prev, currentQuestion.question_id]));
      playSfx("decision_lock");

      // Server-side grading: client no longer has access to correct_answer.
      const selectedOriginal = options.find(o => o.key === key)?.originalKey ?? key;
      const gradePayload = {
        _question_id: currentQuestion.question_id,
        _selected: selectedOriginal,
      };
      console.info("[quiz.grade] request", {
        questionId: currentQuestion.question_id,
        selectedKey: key,
        selectedOriginal,
        payload: gradePayload,
      });
      const { data: gradeData, error: gradeError } = await supabase.rpc(
        "grade_lesson_question",
        gradePayload
      );
      if (gradeError || !gradeData) {
        console.error("[quiz.grade] failed", {
          questionId: currentQuestion.question_id,
          selectedOriginal,
          error: gradeError,
          data: gradeData,
        });
        // Distinguish auth loss (RPC returns 401 / 42501 permission denied
        // when the local session was dropped mid-lesson) from real server
        // failures so learners get an actionable message.
        const code = (gradeError)?.code;
        const status = (gradeError as AnyValue)?.status;
        const isAuthLoss =
          code === "42501" ||
          status === 401 ||
          /permission denied|jwt|unauthor/i.test((gradeError)?.message || "");
        if (isAuthLoss) {
          toast.error("You've been signed out. Please sign in again to save your answer.");
        } else {
          toast.error("Couldn't grade your answer. Please try again.");
        }
        setSelectedAnswer(null);
        setShowExplanation(false);
        return;
      }
      console.info("[quiz.grade] response", {
        questionId: currentQuestion.question_id,
        response: gradeData,
      });
      const grade = gradeData as { is_correct: boolean; correct_answer: string; explanation: string | null; explanation_video_url: string | null };
      const correctOriginal = grade.correct_answer;
      const correctDisplayKey = options.find(o => o.originalKey === correctOriginal)?.key ?? "";
      // Persist the resolved correct display key on this question so the UI
      // and downstream handleNext see the right value.
      setShuffledQuestions(prev => prev.map((sq, i) => i === currentIndex ? { ...sq, correctKey: correctDisplayKey } : sq));
      const correct = grade.is_correct;

      // ---- Adaptive rescue logic (beta lessons only) ----
      // After 2 consecutive misses, soften the NEXT slot by one tier.
      // After 2 consecutive correct answers, restore the next slot toward
      // the originally planned tier (never above the original plan).
      const isBeta = isBetaLesson(lessonId);
      const nextIdx = currentIndex + 1;
      const nextStreak = {
        correct: correct ? adaptiveStreak.correct + 1 : 0,
        miss: correct ? 0 : adaptiveStreak.miss + 1,
      };
      setAdaptiveStreak(nextStreak);
      let plannedTierForCurrent: AdaptiveTier | null = null;
      let servedTierForCurrent: AdaptiveTier | null = null;
      if (isBeta) {
        plannedTierForCurrent = adaptivePlannedTiers[currentIndex] ?? null;
        servedTierForCurrent = adaptiveServedTiers[currentIndex] ?? null;
        if (
          adaptiveRemaining &&
          nextIdx < questions.length &&
          (nextStreak.miss >= 2 || nextStreak.correct >= 2)
        ) {
          const currentServed = adaptiveServedTiers[nextIdx]
            ?? tierOfQuestion(questions[nextIdx] as AnyValue as AdaptiveQuestion);
          const planned = adaptivePlannedTiers[nextIdx] ?? currentServed;
          let targetTier: AdaptiveTier;
          if (nextStreak.miss >= 2) {
            // Soften: one tier below currently-served (never below easy).
            targetTier = shiftTier(currentServed, -1);
          } else {
            // Recover: nudge back toward the planned tier, capped at planned.
            targetTier = shiftTier(currentServed, +1);
            // Don't exceed the original plan during recovery.
            const plannedIdx = ["easy","medium","hard","expert"].indexOf(planned);
            const targetIdx = ["easy","medium","hard","expert"].indexOf(targetTier);
            if (targetIdx > plannedIdx) targetTier = planned;
          }
          if (targetTier !== currentServed) {
            const swap = pickRescueReplacement({
              currentQuestion: questions[nextIdx] as AnyValue as AdaptiveQuestion,
              targetTier,
              remainingByTier: adaptiveRemaining,
            });
            if (swap) {
              const newQ = swap.question as AnyValue as Question;
              setQuestions(prev => prev.map((q, i) => i === nextIdx ? newQ : q));
              setShuffledQuestions(prev => prev.map((sq, i) => {
                if (i !== nextIdx) return sq;
                const { options: opts } = shuffleOptions(newQ);
                return { original: newQ, options: opts, correctKey: "" };
              }));
              setAdaptiveServedTiers(prev => prev.map((t, i) => i === nextIdx ? swap.servedTier : t));
            }
          }
        }
      }

      // Per-question telemetry so parent insights & adaptive engine can see
      // concept-level patterns (concept_tag + adaptive_tier from the question).
      void trackQuiz({
        eventType: correct ? "question_answered_correct" : "question_answered_incorrect",
        lessonId,
        sourceId: currentQuestion.question_id,
        attemptId: String(currentIndex + 1),
        conceptTag: ((currentQuestion as AnyValue).concept_tag as string | null) ?? null,
        difficulty: servedTierForCurrent
          ?? ((currentQuestion as AnyValue).adaptive_tier as string | null)
          ?? ((currentQuestion as AnyValue).difficulty as string | null)
          ?? null,
        correctness: correct,
        timeSpentSeconds: typeof responseTimeMs === "number" ? Math.round(responseTimeMs / 1000) : null,
        metadata: {
          selected_answer: selectedOriginal,
          correct_answer: correctOriginal,
          is_beta_question: !!(currentQuestion as AnyValue).is_beta_question,
          beta_question_version: ((currentQuestion as AnyValue).beta_question_version as string | null) ?? null,
          planned_tier: plannedTierForCurrent,
          served_tier: servedTierForCurrent,
          consecutive_correct: nextStreak.correct,
          consecutive_miss: nextStreak.miss,
        },
      });

      if (correct) {
        setCorrectCount((prev) => prev + 1);
        setFeedbackMessage("Correct! " + (grade.explanation || ""));
        playSfx("correct_soft");
        // Delayed progress bar fill sequel (skipped on completion where money_gain plays).
        setTimeout(() => playSfx("progress_fill"), 280);
        incrementProgress("answer_5_correct");
        // Persistent cash is NEVER paid per question — question count must not
        // inflate the payout. The whole lesson pays once, on completion, via
        // `award_lesson_completion_reward` (max $20).
      } else {
        setFeedbackMessage("Not quite. " + (grade.explanation || ""));
        playSfx("incorrect_soft");
        const correctLetter = correctDisplayKey;
        const selectedText = options.find(o => o.key === key)?.text || key;
        const correctText = options.find(o => o.key === correctLetter)?.text || correctLetter;
        setWrongQuestionIds(prev => new Set([...prev, currentQuestion.question_id]));
        setWrongAnswers(prev => [...prev, {
          question: currentQuestion.question,
          selectedAnswer: selectedText,
          correctAnswer: correctText,
        }]);
      }

      const topic = courseName || "general";
      const { data: existing } = await supabase
        .from("topic_mastery")
        .select("*")
        .eq("user_id", user.id)
        .eq("topic", topic)
        .maybeSingle();

      if (existing) {
        const newScore = correct
          ? Math.min(100, existing.mastery_score + 5)
          : Math.max(0, Number(existing.mastery_score) - 3);
        await runWithSessionRetry(async () =>
          await supabase.from("topic_mastery").update({ mastery_score: newScore }).eq("id", existing.id)
        );
      } else {
        await runWithSessionRetry(async () =>
          await supabase.from("topic_mastery").insert({
            user_id: user.id,
            topic,
            mastery_score: correct ? 5 : 0,
          })
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: effect is keyed to identity/mount only; the omitted values are render-unstable inputs and including them would re-run the fetch/side effect on every render
    [selectedAnswer, currentQuestion, user, lessonId, addFunds, accessTier, courseName, currentIndex, rewardPerCorrect, multiplier, incrementProgress, options, questionStartedAt]
  );

  const handleNext = useCallback(async () => {
    if (currentQuestion?.explanation_video_url && !showExplanationVideo) {
      setShowExplanationVideo(true);
      return;
    }

    setShowExplanationVideo(false);

    if (currentIndex < questions.length - 1) {
      setCurrentIndex((prev) => prev + 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
      setShowAICoaching(false);
      setFeedbackMessage("");
      setHintUsageCount(0);
      setQuestionStartedAt(Date.now());
    } else {
      // NOTE: `correctCount` is already incremented in `handleAnswer` when the
      // learner grades a correct answer, so we must NOT add another +1 here for
      // the final question. Doing so produces score_raw > score_total and the
      // backend `record_lesson_quiz_attempt` RPC rejects it with
      // "quiz score is out of range", blocking legitimate completions.
      const rawCorrect = correctCount;
      const totalQuestions = questions.length;
      const safeCorrect = Math.max(0, Math.min(rawCorrect, totalQuestions));
      const passThreshold = Math.ceil(totalQuestions * 0.8);
      const finalCorrectCount = safeCorrect;
      const passed = didPassRequiredQuiz(finalCorrectCount, totalQuestions);

      if (user && lessonId) {
        if (passed) {
          // Dedupe: only run the lesson completion reward block once per
          // (learner × lesson) per mount. Protects against double-fires from
          // the auto-advance effect, double-clicks, or React StrictMode.
          const rewardEventKey = `${user.id}:${lessonId}:lesson-complete`;
          if (lessonRewardFiredRef.current.has(rewardEventKey)) {
            console.log("Reward event evaluated", {
              learnerId: user.id,
              lessonId,
              rewardEventKey,
              alreadyShown: true,
              source: "handleNext",
            });
            setIsComplete(true);
            return;
          }
          lessonRewardFiredRef.current.add(rewardEventKey);
          console.log("Reward event evaluated", {
            learnerId: user.id,
            lessonId,
            rewardEventKey,
            alreadyShown: false,
            source: "handleNext",
          });
          try {
            const attempt = await recordQuizAttempt(finalCorrectCount, passed);
            if (attempt && !attempt.passed) {
              throw new Error("Quiz attempt did not meet the passing threshold.");
            }
            trackLearningEventOnce("quiz_completed", learningScope, { ...learningCtx, stage: "questions" });
          } catch (error: unknown) {
            lessonRewardFiredRef.current.delete(rewardEventKey);
            toast.error("Couldn't complete lesson", {
              description: getErrorMessage(error) ?? "Your quiz attempt must be saved before completion.",
            });
            return;
          }

          const isPerfect = finalCorrectCount === questions.length && questions.length > 0;

          // ONE canonical persistent payout per lesson: $5 for completing plus
          // up to $15 scaled by the independent score, hard-capped at $20 and
          // computed server-side. Idempotent — retries, remediation, rescue
          // rounds and reloads cannot farm cash, and a perfect score adds no
          // extra cash beyond the cap.
          const { data: lessonReward, error: lessonRewardError } = await supabase.rpc(
            "award_lesson_completion_reward",
            { p_lesson_id: lessonId } as never,
          );

          if (lessonRewardError) {
            console.warn("[LessonScreen] lesson reward rejected:", lessonRewardError.message);
          } else {
            const payload = (lessonReward ?? {}) as { amount?: number; awarded?: boolean; held?: boolean };
            const earned = Number(payload.amount ?? 0);
            setLessonPayout(earned);
            if (payload.held && earned > 0) {
              toast.info(`$${earned} held`, {
                description: "It deposits automatically when your accounts open.",
              });
            } else if (earned > 0) {
              notifyWalletUpdated();
              playSfx("money_gain");
              toast.success(`+$${earned} earned!`);
              incrementProgress("earn_50_dollars", earned);
              incrementProgress("w_earn_200", earned);
            }

          }

          let xpAmount = 40;
          const hasVideo = !!lessonVideoUrl;
          const hasInfographic = !!lessonInfographicUrl;
          if (hasVideo || hasInfographic) xpAmount = 75;
          if (isPerfect) xpAmount += 20;

          const xpResult = await awardXP(xpAmount, isPerfect ? "Perfect lesson completion" : "Lesson completed", "lesson", lessonId);
          const levelUpKey = `${user.id}:${lessonId}:level-up`;
          if (xpResult?.leveledUp && !levelUpShownRef.current.has(levelUpKey)) {
            levelUpShownRef.current.add(levelUpKey);
            setShowLevelUp(true);
          } else if (xpResult && !xpResult.leveledUp) {
            toast.success(`+${xpResult.xp} XP earned!`);
          }

          await recordLearning();

          incrementProgress("complete_2_lessons");
          incrementProgress("w_complete_5_lessons");
          if (isPerfect) {
            incrementProgress("perfect_quiz");
            incrementProgress("w_perfect_3");
          }

          const creditChargeDescriptions = [
            "Study Materials — Digital Textbook",
            "Learning Supplies — Practice Workbook",
            "Educational Software — Module Access",
            "Study Kit — Flashcard Pack",
            "Digital Tools — Note-Taking App",
          ];
          const desc = creditChargeDescriptions[Math.floor(Math.random() * creditChargeDescriptions.length)];
          const chargeAmount = 10 + Math.floor(Math.random() * 21);
          await supabase.rpc("auto_credit_charge", {
            p_amount: chargeAmount,
            p_description: `Credit Purchase: ${desc}`,
          });

          await refetch();
          await clearProgress();
          // lesson_completed fires only after the lesson's persisted
          // completion (quiz attempt + rewards + progress reconciliation)
          // has succeeded — never on the button click itself.
          trackLearningEventOnce("lesson_completed", learningScope, { ...learningCtx, stage: "questions" });

          // Mark mastery recommendation as acted on if it exists
          await supabase.from("ai_mastery_recommendations")
            .update({ acted_on: true })
            .eq("user_id", user.id)
            .eq("target_lesson_id", lessonId)
            .then(() => {});

          const newCompleted = lessonsCompleted + 1;
          setLessonsCompleted(newCompleted);
          // Beta learners follow a curated path with their own Life Event
          // overlay (LaunchLifeEventOverlay) fired by the learner's explicit
          // "Continue" click. We must NOT also trigger the legacy life-event
          // or event-engine systems here — doing so would stack a second
          // modal on top of the preferred Lesson Complete! screen and force
          // the learner away before they can read it.
          if (isLaunchExperience) {
            setIsComplete(true);
          } else {
            const lifeEventTriggered = lifeEvents.checkAndTriggerEvent(lessonId, newCompleted);
            if (!lifeEventTriggered) {
              const eventTriggered = checkAndTriggerEvent(lessonId, newCompleted);
              if (!eventTriggered) {
                setIsComplete(true);
              }
            }
          }
        } else {
          try {
            await recordQuizAttempt(finalCorrectCount, passed);
          } catch (error: unknown) {
            toast.error("Couldn't save quiz attempt", {
              description: getErrorMessage(error) ?? "Please try again.",
            });
            return;
          }
          // quiz_completed: the attempt persisted successfully (pass/fail is
          // a separate dimension; the quiz step itself was completed).
          trackLearningEventOnce("quiz_completed", learningScope, { ...learningCtx, stage: "questions" });
          await clearProgress();
          setIsFailed(true);
        }
      } else {
        toast.error("Sign in required", {
          description: "Your quiz pass must be saved before this lesson can be completed.",
        });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: effect is keyed to identity/mount only; the omitted values are render-unstable inputs and including them would re-run the fetch/side effect on every render
  }, [currentQuestion, showExplanationVideo, currentIndex, questions.length, correctCount, selectedAnswer, correctKey, user, lessonId, addFunds, accessTier, rewardPerCorrect, multiplier, completionBonus, lessonVideoUrl, lessonInfographicUrl, perfectScoreBonus, awardXP, recordLearning, incrementProgress, refetch, clearProgress, lessonsCompleted, checkAndTriggerEvent, recordQuizAttempt]);

  // NOTE: We intentionally do NOT auto-advance after the final answer.
  // Forcing navigation from the quiz feedback to the Lesson Complete screen
  // (or any modal) on a timer was kicking learners out before they could
  // read the feedback or interact with the reward UI. The learner clicks
  // "Complete Lesson →" manually when they're ready.

  const getBoxState = (key: string) => {
    if (!selectedAnswer) return "default";
    // While the server is grading (correctKey === ""), show the chosen
    // answer in a neutral "pending" state — never flash red before the
    // grade arrives. Other options stay default to avoid premature dimming.
    if (!correctKey) {
      return key === selectedAnswer ? "pending" : "default";
    }
    if (key === correctKey) return "correct";
    if (key === selectedAnswer) return "incorrect";
    return "dimmed";
  };

  const boxPositions: ImageBoxPosition[] = [
    { imgLeft: 27, imgTop: 22, imgWidth: 19, imgHeight: 23 },
    { imgLeft: 27, imgTop: 49, imgWidth: 19, imgHeight: 23 },
    { imgLeft: 57, imgTop: 22, imgWidth: 20, imgHeight: 23 },
    { imgLeft: 57, imgTop: 49, imgWidth: 20, imgHeight: 23 },
  ];

  // P0-2 (beta hardening): NEVER auto-complete a lesson when the quiz
  // failed to load or a lesson has zero renderable questions. Previously
  // non-beta learners would receive completion + XP + funds + streak +
  // missions in that case, which corrupts progression and rewards. Since
  // no reliable "quiz-less by design" marker exists on the lessons row
  // today, we refuse to auto-complete for every tier. The universal
  // "Knowledge Check unavailable" fallback screen below handles this
  // case for the learner.
  useEffect(() => {
    if (!dataLoaded || quizLoadError || questions.length > 0 || stage !== "questions" || !lessonName) return;
    console.error(
      "[lesson.completion.guard] Refusing to auto-complete lesson with zero questions",
      { lessonId, lessonName, stage, accessTier, isKnowledgeCheckContext },
    );
  }, [dataLoaded, quizLoadError, questions.length, stage, lessonName, lessonId, accessTier, isKnowledgeCheckContext]);

  // Stage nav bar for video/infographic/reading stages.
  // Two-row layout on mobile (Save & Exit on top, stage tabs below) — single row on sm+
  const stageNavBar = availableStages.length > 1 ? (
    <div
      className="fixed top-0 left-0 right-0 z-50 border-b border-border/50 bg-card/85 backdrop-blur-md"
      style={{
        paddingTop: "env(safe-area-inset-top, 0px)",
        paddingLeft: "env(safe-area-inset-left, 0px)",
        paddingRight: "env(safe-area-inset-right, 0px)",
      }}
    >
      <div className="container mx-auto box-border flex max-w-7xl flex-col gap-1.5 px-3 py-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3 sm:px-4">
        <h1 className="sr-only">{lessonName || "Lesson"}</h1>
        <div className="flex min-w-0 items-center justify-between gap-2 sm:justify-start">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSaveAndExit}
            className="h-11 shrink-0 gap-1.5 px-2 text-xs text-muted-foreground hover:text-foreground sm:h-9"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="whitespace-nowrap">Save & Exit</span>
          </Button>
          {lessonName && (
            <span className="min-w-0 truncate text-right font-heading text-xs font-semibold text-foreground/80 sm:hidden">
              {lessonName}
            </span>
          )}
        </div>
        <div className="min-w-0 sm:flex-1 sm:flex sm:justify-center">
          <LessonStageNav
            currentStage={stage as LessonStageType}
            availableStages={availableStages}
            onStageChange={handleStageChange}
            completedStages={completedStages}
          />
        </div>
        {/* Spacer to keep stage nav centered on desktop */}
        <div className="hidden w-20 sm:block" />
      </div>
    </div>
  ) : null;

  // --- Daily allowance entry gate (before any lesson work) ---
  if (!isComplete && !lessonStartGate.loading && !lessonStartGate.allowed) {
    return (
      <DailyLessonLockScreen
        completedLessonName={lessonStartGate.completedLessonName}
        nextAvailableAt={lessonStartGate.nextAvailableAt}
      />
    );
  }

  // --- Entry shell: hold until media availability + resume are settled (no Insight flash) ---
  if (!resumeApplied && !isComplete && !quizLoadError) {
    return (
      <div
        className="min-h-screen bg-cover bg-center bg-no-repeat flex items-center justify-center"
        style={{ backgroundImage: `url(${classroomBg})` }}
        role="status"
        aria-live="polite"
      >
        <p className="text-foreground/80 holo-text text-lg">Loading lesson…</p>
      </div>
    );
  }

  // --- Stage 1: Video ---
  if (stage === "video" && lessonVideoUrl) {
    return (
      <div className="relative">
        {stageNavBar}
        <div className={stageNavBar ? "pt-24 sm:pt-14" : ""}>
          <VideoStage videoUrl={lessonVideoUrl} lessonName={lessonName} lessonId={lessonId} onContinue={handleVideoComplete} />
        </div>
      </div>
    );
  }

  // --- Stage 2a: Case Study Reading ---
  if (stage === "reading" && caseStudyReading) {
    return (
      <div className="relative">
        {stageNavBar}
        <div className={stageNavBar ? "pt-24 sm:pt-14" : ""}>
          <CaseStudyReadingStage reading={caseStudyReading} onContinue={handleReadingComplete} />
        </div>
      </div>
    );
  }

  // --- Stage 2b: Infographic ---
  // For case studies: always show (with fallback if no URL)
  // For standard: only show if URL exists
  if (stage === "infographic" && (isCaseStudy || lessonInfographicUrl)) {
    return (
      <div className="relative">
        {stageNavBar}
        <div className={stageNavBar ? "pt-24 sm:pt-14" : ""}>
          <InfographicStage
            infographicUrl={lessonInfographicUrl}
            onContinue={handleInfographicComplete}
            lessonId={lessonId ?? null}
            continueLabel={
              availableStages[availableStages.indexOf("infographic") + 1] === "mastery" ? "Continue to Insights →" :
              availableStages[availableStages.indexOf("infographic") + 1] === "decision" ? "Continue to Decision →" :
              "Continue to Quiz →"
            }
          />
        </div>
      </div>
    );
  }

  // --- Stage: Mastery Content (Vocabulary + Book Insight + Company Case) ---
  if (stage === "mastery" && hasMasteryContent) {
    return (
      <div className="relative">
        {stageNavBar}
        <div className={stageNavBar ? "pt-24 sm:pt-14" : ""}>
          <MasteryLessonContent
            lessonName={lessonName}
            courseName={courseName}
            vocabulary={masteryContent.vocabulary}
            bookInsights={masteryContent.bookInsights}
            companyCases={masteryContent.companyCases}
            decisionPrompt={masteryContent.decisionPrompt}
            onContinueToQuiz={handleMasteryComplete}
            onDecisionPrompt={hasDecisionPrompt ? () => {
              setCompletedStages(prev => new Set([...prev, "mastery"]));
              setStage("decision");
            } : undefined}
            lessonData={masteryContent.adaptiveData || undefined}
          />
        </div>
      </div>
    );
  }

  // --- Stage: Decision Prompt ---
  if (stage === "decision" && masteryContent.decisionPrompt) {
    return (
      <div className="relative">
        {stageNavBar}
        <div className={stageNavBar ? "pt-24 sm:pt-14" : ""}>
          <DecisionCheckStage
            lessonId={lessonId!}
            lessonName={lessonName}
            decisionPrompt={masteryContent.decisionPrompt}
            masteryPrinciple={masteryContent.masteryPrinciple}
            bookInsights={masteryContent.bookInsights}
            companyCases={masteryContent.companyCases}
            onPass={handleDecisionComplete}
            onReviewInsight={() => {
              // Allow learner to revisit the Insight page; don't mark decision complete
              setStage("mastery");
            }}
          />
        </div>
      </div>
    );
  }


  // While the V3 rollback flag is resolving, never render a legacy quiz or a
  // legacy "unavailable" screen — the adaptive path may own this stage.
  if (stage === "questions" && !isComplete && (v3FlagLoading || (v3Enabled && lessonId))) {
    if (v3FlagLoading) {
      return (
        <div
          className="min-h-screen bg-cover bg-center bg-no-repeat flex items-center justify-center"
          style={{ backgroundImage: `url(${classroomBg})` }}
        >
          <p className="text-foreground/80 holo-text text-lg">Loading...</p>
        </div>
      );
    }
    return renderV3Assessment();
  }

  const quizContentIssue = dataLoaded && stage === "questions" && !!lessonName && (
    !!quizLoadError ||
    shouldBlockRequiredBetaQuiz({
      dataLoaded,
      lessonId,
      lessonName,
      questionCount: questions.length,
      quizLoadError,
      stage,
    })
  );

  if (quizContentIssue) {
    return (
      <div
        className="min-h-screen bg-cover bg-center bg-no-repeat flex items-center justify-center px-4"
        style={{ backgroundImage: `url(${classroomBg})` }}
      >
        <div className="glass-card glow-border p-8 max-w-md w-full text-center space-y-5">
          <div className="w-14 h-14 rounded-full bg-destructive/15 flex items-center justify-center mx-auto">
            <AlertCircle className="w-7 h-7 text-destructive" />
          </div>
          <div className="space-y-2">
            <h2 className="font-heading font-bold text-xl text-foreground holo-text">
              Knowledge Check unavailable
            </h2>
            <p className="text-sm text-muted-foreground">
              This lesson requires a Knowledge Check, but the questions could not be loaded. Your lesson has not been completed.
            </p>
            {import.meta.env.DEV && (
              <p className="text-xs text-destructive/80 mt-2">
                Admin/dev: {quizLoadError || `Missing quiz questions for lesson "${lessonName}" (${lessonId}).`}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <Button
              onClick={() => window.location.reload()}
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-heading font-semibold"
            >
              Try Again
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                void recordLearningEvent({
                  eventType: "dashboard_returned",
                  lessonId: lessonId ?? null,
                  metadata: { source: "quiz_load_failed_error_screen" },
                });
                navigate("/dashboard");
              }}
              className="w-full"
            >
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // --- Quiz Failed screen (didn't meet 4/5 threshold) ---
  if (isFailed) {
    const passThreshold = Math.ceil(questions.length * 0.8);
    const handleRetry = () => {
      setIsFailed(false);
      // New quiz attempt — advance the analytics attempt scope so
      // quiz_started/quiz_completed for the retry are not deduped away.
      setAttemptSeq((n) => n + 1);
      setCurrentIndex(0);
      setSelectedAnswer(null);
      setShowExplanation(false);
      setFeedbackMessage("");
      setCorrectCount(0);
      setAnsweredIds(new Set());
      setWrongAnswers([]);
      setWrongQuestionIds(new Set());
      setShuffledQuestions(questions.map(q => {
        const { options } = shuffleOptions(q);
        return { original: q, options, correctKey: "" };
      }));
    };
    return (
      <div
        className="min-h-screen bg-cover bg-center bg-no-repeat flex items-center justify-center p-4"
        style={{ backgroundImage: `url(${classroomBg})` }}
      >
        <div className="max-w-md w-full space-y-4">
          <div className="glass-card glow-border p-8 text-center space-y-6">
            <div className="w-16 h-16 rounded-full bg-destructive/20 flex items-center justify-center mx-auto">
              <ArrowLeft className="w-8 h-8 text-destructive" />
            </div>
            <div className="space-y-2">
              <h2 className="font-heading font-bold text-2xl text-foreground holo-text">Not Quite There Yet</h2>
              <p className="text-muted-foreground">
                You got {correctCount} out of {questions.length} correct. You need at least {passThreshold} correct to pass.
              </p>
              <p className="text-sm text-muted-foreground/80 leading-relaxed">
                Don't worry — review the concepts and try again. Every attempt helps you learn!
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <Button
                onClick={handleRetry}
                className="w-full bg-primary hover:bg-primary/90"
              >
                Retry Knowledge Check
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/dashboard")}
                className="w-full border-border text-muted-foreground hover:bg-muted/30"
              >
                {"Back to Dashboard"}
              </Button>
            </div>
          </div>

          {/* AI Remediation Card */}
          <AIRemediationCard
            lessonId={lessonId}
            lessonName={lessonName}
            courseName={courseName}
            score={correctCount}
            totalQuestions={questions.length}
            wrongAnswers={wrongAnswers}
            onReviewLesson={() => navigate("/dashboard")}
            onAskAI={() => setShowTutor(true)}
          />
        </div>
      </div>
    );
  }

  // --- Completion screen ---
  if (isComplete) {
    // Find next lesson in course
    const handleContinueToNext = async () => {
      if (!courseName) return;
      // Beta path: inject a Life Event interruption before advancing.
      // Life-event surfacing is beta-only UX, not part of route resolution.
      if (isLaunchExperience && lessonId) {
        const ev = getLifeEventForLesson(lessonId);
        if (ev && !hasSeenLifeEvent(ev.id)) {
          setPendingLifeEvent(ev);
          return;
        }
      }
      // Unified official-path resolver — routing only, no state mutation.
      const { route } = await resolveNextLessonRoute({
        accessTier,
        currentLessonId: lessonId,
        courseName,
        betaLaunchItems: launchPathItems,
      });
      navigate(route);
    };

    return (
      <div
        className="min-h-screen overflow-y-auto bg-cover bg-center bg-no-repeat flex items-center justify-center px-4 py-6 sm:py-10"
        style={{ backgroundImage: `url(${classroomBg})` }}
      >
        <div className="glass-card glow-border w-full max-w-lg text-center space-y-5 p-5 sm:space-y-6 sm:p-8 my-auto">
          <div className="w-16 h-16 rounded-full bg-accent/20 flex items-center justify-center mx-auto">
            <Trophy className="w-8 h-8 text-accent" />
          </div>
          <div className="space-y-2">
            <h2 className="font-heading font-bold text-2xl text-foreground holo-text">Lesson Complete!</h2>
            {questions.length > 0 ? (
              <p className="text-muted-foreground">
                You got {correctCount} out of {questions.length} correct
              </p>
            ) : (
              <p className="text-muted-foreground">
                Lesson finished — your progress is saved.
              </p>
            )}
            {lessonPayout > 0 && (
              <p className="text-lg font-heading font-bold text-accent holo-text">
                +${lessonPayout} earned!
              </p>
            )}
          </div>
          <CompletionTakeaway
            lessonName={lessonName}
            takeaway={masteryContent.completionTakeaway}
            masteryPrinciple={masteryContent.masteryPrinciple}
          />
          {isLaunchExperience && lessonId && isLaunchLessonId(lessonId) && (() => {
            const outcome = correctCount >= Math.ceil(questions.length * 0.7) ? "success" : "mistake";
            const coaching = getLumiLessonOutcome(lessonId, outcome);
            return coaching ? (
              <LumiCoachingCard line={coaching} variant="inline" contextId={lessonId} />
            ) : null;
          })()}
          {(() => {
            const isBeta = isLaunchExperience;
            const showBetaContinue = isBeta ? hasNextBeta : true;
            return (
          <div className="flex flex-col gap-3">
            {isBeta && launchPathLoading && (
              <Button disabled className="w-full font-heading font-semibold">
                Preparing next step...
              </Button>
            )}
            {isBeta && !launchPathLoading && nextBetaRouteUnavailable && (
              <p className="rounded-xl border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                Next beta step is misconfigured. Return to the dashboard while this is fixed.
              </p>
            )}
            {(!isBeta || showBetaContinue) && (
              <Button
                onClick={handleContinueToNext}
                className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-heading font-semibold shadow-[0_0_20px_hsl(var(--accent)/0.3)] gap-2"
              >
                {isBeta ? continueButtonLabel : "Continue to Next Lesson"}
                <ArrowRight className="w-4 h-4" />
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setExploreOpen(true)}
              className="w-full gap-2 border-accent/30 bg-accent/5 text-foreground hover:bg-accent/10"
            >
              <Globe2 className="w-4 h-4 text-accent" />
              Explore this concept in the real world
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/dashboard")}
              className="w-full border-border text-muted-foreground hover:bg-muted/30"
            >
              {"Back to Dashboard"}
            </Button>
            <Button
              variant="ghost"
              onClick={() => setRetakeDialogOpen(true)}
              className="w-full text-muted-foreground hover:text-foreground"
            >
              Retake Lesson
            </Button>
          </div>
            );
          })()}
        </div>
        <RetakeLessonDialog
          open={retakeDialogOpen}
          onOpenChange={setRetakeDialogOpen}
          onConfirm={handleConfirmLessonRetake}
          pending={retakePending}
        />
        {showLevelUp && lastAward && (
          <LevelUpModal level={playerLevel.current_level} title={playerLevel.current_title} xpEarned={lastAward.xp} onDismiss={() => { setShowLevelUp(false); clearLastAward(); }} />
        )}
        <ExploreModal
          open={exploreOpen}
          onOpenChange={setExploreOpen}
          lessonId={lessonId!}
          lessonName={lessonName}
        />
        {pendingLifeEvent && (
          <LaunchLifeEventOverlay
            event={pendingLifeEvent}
            onContinue={() => {
              setPendingLifeEvent(null);
              navigateToNextBetaRoute();
            }}
          />
        )}
      </div>
    );
  }

  // --- No questions completion screen ---
  if (dataLoaded && questions.length === 0 && stage === "questions" && lessonName && isComplete) {
    const handleContinueToNextNoQuiz = async () => {
      if (!courseName) return;
      if (isLaunchExperience && lessonId) {
        const ev = getLifeEventForLesson(lessonId);
        if (ev && !hasSeenLifeEvent(ev.id)) {
          setPendingLifeEvent(ev);
          return;
        }
      }
      const { route } = await resolveNextLessonRoute({
        accessTier,
        currentLessonId: lessonId,
        courseName,
        betaLaunchItems: launchPathItems,
      });
      navigate(route);
    };

    return (
      <div
        className="min-h-screen bg-cover bg-center bg-no-repeat flex items-center justify-center"
        style={{ backgroundImage: `url(${classroomBg})` }}
      >
        <div className="glass-card glow-border p-8 max-w-md w-full text-center space-y-6">
          <div className="w-16 h-16 rounded-full bg-accent/20 flex items-center justify-center mx-auto">
            <Trophy className="w-8 h-8 text-accent" />
          </div>
          <div className="space-y-2">
            <h2 className="font-heading font-bold text-2xl text-foreground holo-text">Lesson Complete!</h2>
            <p className="text-muted-foreground">This lesson had no quiz questions.</p>
            <p className="text-lg font-heading font-bold text-accent holo-text">Your progress is saved.</p>
          </div>
          {(() => {
            const isBeta = isLaunchExperience;
            const showBetaContinue = isBeta ? hasNextBeta : true;
            return (
          <div className="flex flex-col gap-3">
            {(!isBeta || showBetaContinue) && (
              <Button
                onClick={handleContinueToNextNoQuiz}
                className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-heading font-semibold shadow-[0_0_20px_hsl(var(--accent)/0.3)] gap-2"
              >
                {isBeta ? continueButtonLabel : "Continue to Next Lesson"}
                <ArrowRight className="w-4 h-4" />
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => navigate("/dashboard")}
              className="w-full border-border text-muted-foreground hover:bg-muted/30"
            >
              {"Back to Dashboard"}
            </Button>
          </div>
            );
          })()}
        </div>
        {showLevelUp && lastAward && (
          <LevelUpModal level={playerLevel.current_level} title={playerLevel.current_title} xpEarned={lastAward.xp} onDismiss={() => { setShowLevelUp(false); clearLastAward(); }} />
        )}
        {pendingLifeEvent && (
          <LaunchLifeEventOverlay
            event={pendingLifeEvent}
            onContinue={() => {
              setPendingLifeEvent(null);
              navigateToNextBetaRoute();
            }}
          />
        )}
      </div>
    );
  }

  // --- No questions on the quiz stage: universal Knowledge Check
  // unavailable fallback. P0-2: no auto-completion, no XP, no funds, no
  // streak, no mission increment, no unlock — for any tier. Applies
  // whether the quiz explicitly failed to load or simply returned zero
  // rows, since we have no reliable "quiz-less by design" marker today.
  if (dataLoaded && questions.length === 0 && stage === "questions" && lessonName && !isComplete) {
    return (
      <div
        className="min-h-screen bg-cover bg-center bg-no-repeat flex items-center justify-center px-4"
        style={{ backgroundImage: `url(${classroomBg})` }}
      >
        <div className="glass-card glow-border p-8 max-w-md w-full text-center space-y-5">
          <div className="w-14 h-14 rounded-full bg-destructive/15 flex items-center justify-center mx-auto">
            <AlertCircle className="w-7 h-7 text-destructive" />
          </div>
          <div className="space-y-2">
            <h2 className="font-heading font-bold text-xl text-foreground holo-text">
              Knowledge Check unavailable
            </h2>
            <p className="text-sm text-muted-foreground">
              This lesson's Knowledge Check couldn't be loaded, so the lesson has not been completed.
            </p>
            {import.meta.env.DEV && (
              <p className="text-xs text-destructive/80 mt-2">
                Admin/dev: Missing quiz questions for lesson "{lessonName}" ({lessonId}).
              </p>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <Button
              onClick={() => window.location.reload()}
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-heading font-semibold"
            >
              Try Again
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                void recordLearningEvent({
                  eventType: "dashboard_returned",
                  lessonId: lessonId ?? null,
                  metadata: { source: "quiz_empty_state_error_screen" },
                });
                navigate("/dashboard");
              }}
              className="w-full"
            >
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // --- V3 adaptive assessment path (rollback flag: engine_rules.v3_assessment_default) ---
  function renderV3Assessment() {
    return (
      <div
        className="min-h-screen bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${classroomBg})` }}
      >
        <div className="min-h-screen bg-background/80 backdrop-blur-sm py-8">
          <AdaptiveAssessmentStage
            lessonId={lessonId!}
            hintUsed={hintUsageCount > 0}
            onExit={() => navigate("/dashboard")}
            onComplete={async (sufficient) => {
              if (sufficient) {
                const { data } = await supabase.rpc("engine_complete_v3_lesson", {
                  p_lesson_id: lessonId,
                });
                const result = (data ?? {}) as {
                  reward?: { awarded?: boolean; held?: boolean; amount?: number; reason?: string };
                };
                const reward = result.reward;
                if (reward?.awarded) {
                  toast.success("Lesson complete", {
                    description: `$${Number(reward.amount ?? 0).toFixed(2)} deposited to your Checking account.`,
                  });
                } else if (reward?.held) {
                  toast.info("Lesson complete", {
                    description: `$${Number(reward.amount ?? 0).toFixed(2)} is being held and deposits automatically when your accounts open.`,
                  });
                }

              }
              navigate("/dashboard");
            }}
          />
        </div>
      </div>
    );
  }

  // --- Loading ---
  if (!currentQuestion) {
    return (
      <div
        className="min-h-screen bg-cover bg-center bg-no-repeat flex items-center justify-center"
        style={{ backgroundImage: `url(${classroomBg})` }}
      >
        <p className="text-foreground/80 holo-text text-lg">Loading...</p>
      </div>
    );
  }

  // --- Stage 3: Questions ---
  return (
    <div className="relative overflow-hidden" style={{ minHeight: '100dvh' }}>
      <div
        className="fixed inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${classroomBg})` }}
      />
      {(() => {
        const holo =
          getLumiHolocardMomentsFor(lessonId, "pre_decision")[0] ||
          getLumiHolocardMomentsFor(lessonId, "post_decision")[0] ||
          getLumiHolocardMomentsFor(lessonId)[0];
        return holo ? <LumiHolocard momentId={holo.momentId} title={lessonName} /> : null;
      })()}

      <header
        className="fixed top-0 left-0 right-0 z-50 border-b border-border/60 bg-card/75 backdrop-blur-md"
        style={{
          paddingTop: 'env(safe-area-inset-top, 0px)',
          paddingLeft: 'env(safe-area-inset-left, 0px)',
          paddingRight: 'env(safe-area-inset-right, 0px)',
        }}
      >
        {/* Row 1 (mobile): Stage navigation - full width */}
        {isMobile && availableStages.length > 1 && (
          <div className="w-full border-b border-border/40 px-2 py-1.5 flex items-center justify-center">
            <LessonStageNav
              currentStage="questions"
              availableStages={availableStages}
              onStageChange={handleStageChange}
              completedStages={completedStages}
            />
          </div>
        )}
        {/* Row 2: Controls */}
        <div className="mx-auto box-border flex w-full max-w-7xl items-center justify-between gap-2 px-3 py-2 sm:gap-3 sm:px-4 sm:py-3">
          <div className="flex min-w-0 items-center gap-1 sm:gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleSaveAndExit}
              className="h-11 w-11 shrink-0 text-foreground/75 hover:bg-muted/60 hover:text-foreground sm:h-9 sm:w-9"
              title="Save & Exit"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <span className="min-w-0 max-w-[110px] truncate font-heading text-xs font-semibold text-foreground sm:max-w-[160px] sm:text-sm md:max-w-none">
              {lessonName}
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-1 sm:gap-2">
            <QuickHintButton
              lessonId={lessonId!}
              lessonName={lessonName}
              courseName={courseName}
              currentQuestion={currentQuestion?.question}
              className="shrink-0"
              onHintUsed={() => setHintUsageCount((count) => count + 1)}
            />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowTutor(true)}
              className="h-11 w-11 shrink-0 text-primary/70 hover:bg-primary/10 hover:text-primary sm:h-9 sm:w-9"
              title="Ask AI Tutor"
            >
              <Sparkles className="h-4 w-4" />
            </Button>
            {/* Stage nav inline on desktop only */}
            {!isMobile && availableStages.length > 1 && (
              <LessonStageNav
                currentStage="questions"
                availableStages={availableStages}
                onStageChange={handleStageChange}
                completedStages={completedStages}
              />
            )}
            <span className="shrink-0 whitespace-nowrap text-[11px] font-medium text-accent/90 sm:text-xs">
              {currentIndex + 1}/{questions.length}
            </span>
            <WalletDisplay balance={balance} />
          </div>
        </div>
        {/* Progress bar */}
        <div className="h-1 bg-muted/70">
          <div
            className="h-full bg-accent transition-all duration-500 rounded-r-full shadow-[0_0_10px_hsl(var(--accent)/0.5)]"
            style={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }}
          />
        </div>
      </header>

      {/* ─── MOBILE LAYOUT ─── */}
      {isMobile ? (
        <div
          className="relative z-10 flex flex-col px-3 overflow-y-auto"
          style={{
            minHeight: '100dvh',
            paddingTop: `calc(${availableStages.length > 1 ? '8.5rem' : '6rem'} + env(safe-area-inset-top, 0px))`,
            paddingBottom: 'calc(7.5rem + env(safe-area-inset-bottom, 0px))',
          }}
        >
          {/* Question - dedicated readable container */}
          <QuestionCard question={currentQuestion.question} variant="mobile" />

          {/* Answer grid - separate from question */}
          <div className="grid grid-cols-1 gap-2.5 shrink-0">
            {options.map((option) => {
              const state = getBoxState(option.key);
              const stateStyles: Record<string, string> = {
                default:
                  "border-cyan-300/30 bg-gradient-to-br from-cyan-400/8 via-slate-900/35 to-violet-500/8 hover:border-cyan-200/55 hover:from-cyan-400/14 hover:to-violet-500/14 hover:shadow-[0_0_38px_-6px_hsl(190_90%_60%/0.45)] cursor-pointer",
                pending:
                  "border-cyan-300/70 bg-gradient-to-br from-cyan-400/18 via-slate-900/40 to-primary/15 shadow-[0_0_38px_-4px_hsl(190_90%_60%/0.55)]",
                correct:
                  "border-emerald-300/80 bg-gradient-to-br from-emerald-400/20 via-slate-900/40 to-cyan-400/15 shadow-[0_0_44px_-4px_hsl(160_75%_55%/0.6)]",
                incorrect:
                  "border-rose-300/75 bg-gradient-to-br from-rose-400/16 via-slate-900/40 to-rose-500/10 shadow-[0_0_36px_-4px_hsl(0_75%_65%/0.5)]",
                dimmed:
                  "border-white/10 bg-slate-900/30 opacity-40 cursor-not-allowed",
              };
              return (
                <button
                  key={option.key}
                  onClick={() => handleAnswer(option.key)}
                  disabled={state === "dimmed" || state === "pending" || state === "correct" || state === "incorrect"}
                  className={`group relative overflow-hidden rounded-2xl border backdrop-blur-2xl transition-all duration-300 ease-out grid grid-cols-[auto_1fr] items-center gap-3 pl-4 pr-4 py-3.5 text-left shadow-[0_10px_30px_-12px_rgba(0,0,0,0.55)] ${stateStyles[state]}`}
                >
                  <span aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-200/50 to-transparent" />
                  <span aria-hidden className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-white/8" />
                  <span aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/[0.06] to-transparent" />
                  <span className="relative inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-cyan-300/40 bg-cyan-400/10 text-cyan-200 font-heading font-bold text-[12px] [text-shadow:_0_1px_6px_rgba(0,0,0,0.6)]">{option.key}</span>
                  <span className="relative text-white/95 font-heading font-medium text-sm leading-snug break-words [text-shadow:_0_1px_8px_rgba(0,0,0,0.55)]">
                    {option.text}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Explanation - separate container below answers */}
          {showExplanation && selectedAnswer && currentQuestion && (
            <div className="mt-4 mb-2 animate-fade-in shrink-0 space-y-3">
              <div
                className={`relative overflow-hidden rounded-2xl border-2 p-4 text-center backdrop-blur-xl shadow-[0_0_34px_-6px_hsl(var(--primary)/0.4)] ${
                  correctKey && selectedAnswer === correctKey
                    ? "border-emerald-400/70 bg-gradient-to-br from-emerald-500/12 via-background/85 to-emerald-900/20"
                    : correctKey
                    ? "border-rose-400/70 bg-gradient-to-br from-rose-500/12 via-background/85 to-rose-900/20"
                    : "border-primary/50 bg-gradient-to-br from-primary/10 via-background/85 to-background/80"
                }`}
              >
                <span aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />
                {correctKey && (() => {
                  const isRight = selectedAnswer === correctKey;
                  return (
                    <div className="mb-3 flex items-center justify-center gap-2">
                      <span className={`inline-flex h-7 w-7 items-center justify-center rounded-full border backdrop-blur-md ${
                        isRight
                          ? "border-emerald-300/70 bg-emerald-400/15 text-emerald-200 shadow-[0_0_18px_-2px_hsl(160_75%_55%/0.65)]"
                          : "border-rose-300/70 bg-rose-400/15 text-rose-200 shadow-[0_0_18px_-2px_hsl(0_75%_65%/0.55)]"
                      }`}>
                        {isRight ? <CheckCircle2 className="h-4 w-4" strokeWidth={2.5} /> : <AlertCircle className="h-4 w-4" strokeWidth={2.5} />}
                      </span>
                      <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-heading font-bold uppercase tracking-[0.22em] backdrop-blur-md ${
                        isRight
                          ? "border-emerald-300/45 bg-emerald-400/10 text-emerald-100 [text-shadow:_0_1px_8px_hsl(160_75%_45%/0.6)]"
                          : "border-rose-300/45 bg-rose-400/10 text-rose-100 [text-shadow:_0_1px_8px_hsl(0_75%_55%/0.55)]"
                      }`}>
                        {isRight ? "Correct · Lesson Check" : "Not Quite · Lesson Check"}
                      </span>
                    </div>
                  );
                })()}
                <p className="text-foreground text-sm font-body leading-relaxed mb-3">
                  {feedbackMessage}
                </p>
                <div className="flex flex-wrap items-center justify-center gap-2">
                  <button
                    onClick={handleNext}
                    className="px-6 py-2 rounded-xl border border-accent/50 bg-accent/15 text-accent font-heading font-semibold text-sm hover:bg-accent/25 transition-all duration-200"
                  >
                    {isLastQuestion ? "Complete Lesson →" : "Next Question →"}
                  </button>
                  {!isLastQuestion && (
                    <button
                      onClick={() => setShowAICoaching((v) => !v)}
                      className="px-4 py-2 rounded-xl border border-cyan-300/60 bg-cyan-400/15 text-cyan-100 font-heading font-semibold text-sm hover:bg-cyan-400/25 hover:border-cyan-200/80 transition-all duration-200 [text-shadow:_0_1px_8px_rgba(0,0,0,0.6)]"
                    >
                      {showAICoaching ? "Hide explanation" : "Explain more"}
                    </button>
                  )}
                </div>
              </div>

              {showAICoaching && !isLastQuestion && (
                <AIQuizExplanationPanel
                  lessonId={lessonId!}
                  lessonName={lessonName}
                  courseName={courseName}
                  questionId={currentQuestion.question_id}
                  questionText={currentQuestion.question}
                  answerOptions={options}
                  selectedAnswer={selectedAnswer}
                  correctAnswer={correctKey}
                  staticExplanation={currentQuestion.explanation}
                  hintUsageCount={hintUsageCount}
                  responseTimeMs={responseTimeMs}
                />
              )}

            </div>
          )}
        </div>
      ) : (
        /* ─── DESKTOP LAYOUT (responsive flow over background) ─── */
        <div
          className="relative z-10 mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 overflow-y-auto"
          style={{
            minHeight: '100dvh',
            paddingTop: 'calc(7rem + env(safe-area-inset-top, 0px))',
            paddingBottom: 'calc(4rem + env(safe-area-inset-bottom, 0px))',
          }}
        >
          <div className="mx-auto w-full max-w-4xl">
            <QuestionCard question={currentQuestion.question} variant="desktop" />
          </div>

          <div className="mx-auto -mt-2 grid w-full max-w-4xl grid-cols-2 gap-5">
            {options.map((option) => {
              const state = getBoxState(option.key);
              const stateStyles: Record<string, string> = {
                default:
                  "border-cyan-300/30 bg-gradient-to-br from-cyan-400/8 via-slate-900/35 to-violet-500/8 hover:border-cyan-200/60 hover:from-cyan-400/14 hover:to-violet-500/14 hover:shadow-[0_0_50px_-6px_hsl(190_90%_60%/0.5)] cursor-pointer",
                pending:
                  "border-cyan-300/75 bg-gradient-to-br from-cyan-400/18 via-slate-900/40 to-primary/15 shadow-[0_0_50px_-4px_hsl(190_90%_60%/0.6)]",
                correct:
                  "border-emerald-300/85 bg-gradient-to-br from-emerald-400/20 via-slate-900/40 to-cyan-400/15 shadow-[0_0_56px_-4px_hsl(160_75%_55%/0.65)]",
                incorrect:
                  "border-rose-300/80 bg-gradient-to-br from-rose-400/16 via-slate-900/40 to-rose-500/10 shadow-[0_0_48px_-4px_hsl(0_75%_65%/0.55)]",
                dimmed:
                  "border-white/10 bg-slate-900/30 opacity-40 cursor-not-allowed",
              };

              return (
                <button
                  key={option.key}
                  onClick={() => handleAnswer(option.key)}
                  disabled={state === "dimmed" || state === "pending" || state === "correct" || state === "incorrect"}
                  className={`group relative overflow-hidden min-h-[120px] w-full rounded-2xl border backdrop-blur-2xl transition-all duration-300 ease-out grid grid-cols-[auto_1fr] items-center gap-4 pl-5 pr-6 py-5 md:py-6 text-left shadow-[0_18px_50px_-18px_rgba(0,0,0,0.6)] ${stateStyles[state]}`}
                >
                  <span aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-200/55 to-transparent" />
                  <span aria-hidden className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-white/10" />
                  <span aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/[0.07] to-transparent" />
                  <span aria-hidden className="pointer-events-none absolute -inset-x-1 -top-1 h-10 bg-gradient-to-r from-transparent via-white/15 to-transparent translate-x-[-120%] group-hover:translate-x-[120%] transition-transform duration-700 ease-out blur-sm" />
                  <span className="relative inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-cyan-300/45 bg-cyan-400/10 text-cyan-200 font-heading font-bold text-[14px] [text-shadow:_0_1px_6px_rgba(0,0,0,0.6)]">
                    {option.key}
                  </span>
                  <span className="relative text-white/95 font-heading font-semibold text-base md:text-lg leading-snug break-words [text-shadow:_0_1px_10px_rgba(0,0,0,0.55)]">
                    {option.text}
                  </span>
                </button>
              );
            })}
          </div>

          {showExplanation && selectedAnswer && currentQuestion && (
            <div className="mx-auto w-full max-w-2xl animate-fade-in space-y-3">
              <div
                className={`relative overflow-hidden rounded-2xl border-2 p-4 md:p-5 text-center backdrop-blur-xl shadow-[0_0_38px_-6px_hsl(var(--primary)/0.4)] ${
                  correctKey && selectedAnswer === correctKey
                    ? "border-emerald-400/70 bg-gradient-to-br from-emerald-500/12 via-background/85 to-emerald-900/20"
                    : correctKey
                    ? "border-rose-400/70 bg-gradient-to-br from-rose-500/12 via-background/85 to-rose-900/20"
                    : "border-primary/50 bg-gradient-to-br from-primary/10 via-background/85 to-background/80"
                }`}
              >
                <span aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />
                {correctKey && (() => {
                  const isRight = selectedAnswer === correctKey;
                  return (
                    <div className="mb-3 flex items-center justify-center gap-2.5">
                      <span className={`inline-flex h-8 w-8 items-center justify-center rounded-full border backdrop-blur-md ${
                        isRight
                          ? "border-emerald-300/70 bg-emerald-400/15 text-emerald-200 shadow-[0_0_22px_-2px_hsl(160_75%_55%/0.7)]"
                          : "border-rose-300/70 bg-rose-400/15 text-rose-200 shadow-[0_0_22px_-2px_hsl(0_75%_65%/0.6)]"
                      }`}>
                        {isRight ? <CheckCircle2 className="h-[18px] w-[18px]" strokeWidth={2.5} /> : <AlertCircle className="h-[18px] w-[18px]" strokeWidth={2.5} />}
                      </span>
                      <span className={`inline-flex items-center rounded-full border px-3 py-1 text-[12px] font-heading font-bold uppercase tracking-[0.24em] backdrop-blur-md ${
                        isRight
                          ? "border-emerald-300/50 bg-emerald-400/10 text-emerald-100 [text-shadow:_0_1px_10px_hsl(160_75%_45%/0.7)]"
                          : "border-rose-300/50 bg-rose-400/10 text-rose-100 [text-shadow:_0_1px_10px_hsl(0_75%_55%/0.6)]"
                      }`}>
                        {isRight ? "Correct · Lesson Check" : "Not Quite · Lesson Check"}
                      </span>
                    </div>
                  );
                })()}
                <p className="text-foreground text-sm md:text-base font-body leading-relaxed mb-4">
                  {feedbackMessage}
                </p>
                <div className="flex flex-wrap items-center justify-center gap-2">
                  <button
                    onClick={handleNext}
                    className="px-6 py-2 rounded-xl border border-accent/50 bg-accent/15 text-accent font-heading font-semibold text-sm hover:bg-accent/25 hover:shadow-[0_0_20px_hsl(var(--accent)/0.3)] transition-all duration-200"
                  >
                    {isLastQuestion ? "Complete Lesson →" : "Next Question →"}
                  </button>
                  {!isLastQuestion && (
                    <button
                      onClick={() => setShowAICoaching((v) => !v)}
                      className="px-4 py-2 rounded-xl border border-cyan-300/60 bg-cyan-400/15 text-cyan-100 font-heading font-semibold text-sm hover:bg-cyan-400/25 hover:border-cyan-200/80 hover:shadow-[0_0_20px_hsl(190_90%_60%/0.4)] transition-all duration-200 [text-shadow:_0_1px_8px_rgba(0,0,0,0.6)]"
                    >
                      {showAICoaching ? "Hide explanation" : "Explain more"}
                    </button>
                  )}
                </div>
              </div>

              {showAICoaching && !isLastQuestion && (
                <AIQuizExplanationPanel
                  lessonId={lessonId!}
                  lessonName={lessonName}
                  courseName={courseName}
                  questionId={currentQuestion.question_id}
                  questionText={currentQuestion.question}
                  answerOptions={options}
                  selectedAnswer={selectedAnswer}
                  correctAnswer={correctKey}
                  staticExplanation={currentQuestion.explanation}
                  hintUsageCount={hintUsageCount}
                  responseTimeMs={responseTimeMs}
                />
              )}

            </div>
          )}
        </div>
      )}

      {/* Explanation video overlay */}
      {showExplanationVideo && currentQuestion?.explanation_video_url && (
        <ExplanationVideo
          videoUrl={currentQuestion.explanation_video_url}
          onClose={() => {
            setShowExplanationVideo(false);
            if (currentIndex < questions.length - 1) {
              setCurrentIndex((prev) => prev + 1);
              setSelectedAnswer(null);
              setShowExplanation(false);
              setFeedbackMessage("");
            } else {
              handleNext();
            }
          }}
        />
      )}

      {/* New Life Event System Overlay — non-beta only.
          Beta learners use LaunchLifeEventOverlay triggered by Continue. */}
      {!isLaunchExperience && lifeEvents.activeEvent && (
        <LifeEventCard
          event={lifeEvents.activeEvent}
          onResolve={lifeEvents.resolveEvent}
          onDismiss={() => {
            lifeEvents.dismissEvent();
            refetch();
            setIsComplete(true);
          }}
        />
      )}

      {/* Legacy Event System Overlay — non-beta only. */}
      {!isLaunchExperience && activeEvent && !lifeEvents.activeEvent && (
        <EventCard
          event={activeEvent}
          onResolve={resolveEvent}
          onDismiss={() => {
            dismissEvent();
            refetch();
            setIsComplete(true);
          }}
        />
      )}

      {/* Level Up Modal */}
      {showLevelUp && lastAward && (
        <LevelUpModal
          level={playerLevel.current_level}
          title={playerLevel.current_title}
          xpEarned={lastAward.xp}
          onDismiss={() => {
            setShowLevelUp(false);
            clearLastAward();
          }}
        />
      )}

      {/* Lumi intro coaching — beta path lessons only, once per lesson per learner */}
      {isLaunchExperience && lessonId && isLaunchLessonId(lessonId) && currentIndex === 0 && !selectedAnswer && (() => {
        const intro = getLumiLessonIntro(lessonId);
        return intro ? (
          <div className="fixed left-3 bottom-[2rem] z-40 w-[min(22rem,calc(100vw-1.5rem))] sm:left-4 sm:bottom-[1.5rem]">
            <LumiCoachingCard
              line={intro}
              variant="compact"
              dismissKey={`lesson-intro:${lessonId}`}
              contextId={lessonId}
            />
          </div>
        ) : null;
      })()}

      {/* AI Tutor Drawer */}
      {showTutor && (
        <AITutorDrawer
          lessonId={lessonId!}
          lessonName={lessonName}
          courseName={courseName}
          currentQuestion={currentQuestion?.question}
          onClose={() => setShowTutor(false)}
        />
      )}

      {/* Explore modal — available from inline "Explore in the real world" button too */}
      {lessonId && (
        <ExploreModal
          open={exploreOpen}
          onOpenChange={setExploreOpen}
          lessonId={lessonId}
          lessonName={lessonName}
        />
      )}
    </div>
  );
};

export default LessonScreen;
